import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import cns from 'classnames';

const Colors = ['alarm', 'warning', 'success', 'info', 'safe'];

/**
Le progress servono per informare l’utente circa il tempo di attesa di un’operazione.
Quando il tempo preciso non si conosce si utilizzano quelle indeterminate.
 */
const Progress = ({
  className, indeterminate, percentage, text, color,
}) => {
  const percentageValues = useMemo(() => {
    const values = Array.isArray(percentage) ? percentage : [percentage];
    let total = 0;
    return values.map((v, i) => {
      const probObj = typeof v === 'number' ? { percentage: v } : v;
      const obj = {
        percentage: probObj.percentage + total,
        key: i,
        className: Colors.includes(probObj.color) ? probObj.color : undefined,
        color: probObj.color && probObj.color.startsWith('#') ? probObj.color : undefined,
      };
      total += probObj.percentage;
      return obj;
    });
  }, [percentage]);

  return (
    <div className={cns(
      'progress',
      {
        'progress--indeterminate': indeterminate,
      },
      className,
    )}
    >
      {
        !indeterminate && percentageValues.map((p, i, arr) => (
          <div
            key={p.key}
            className="progress__bar-item"
          >
            <div
              className={cns('progress__bar', 'progress__bar--item', p.className)}
              style={{
                width: `${p.percentage}%`,
                zIndex: 2 * (arr.length - i) + 1,
                backgroundColor: p.color,
              }}
            />
            <div
              className="progress__bar progress__bar--separator"
              style={{
                width: `${p.percentage}%`,
                zIndex: 2 * (arr.length - i),
              }}
            />
          </div>
        ))
      }
      <div
        className="progress__bar"
        style={{ width: indeterminate ? '0%' : (`${percentage}%`) }}
      />
      <div
        className={cns('progress__bufferbar', (Colors.includes(color) ? color : undefined))}
        style={{ width: '100%', backgroundColor: (color && color.startsWith('#') ? color : undefined) }}
      />
      {text && <div className="progress__text">{text}</div>}
    </div>
  );
};

Progress.propTypes = {
  /** Classi css personalizzate da aggiungere al componente */
  className: PropTypes.string,
  /** Indica se mostrare una progress bar animata senza una percentuale precisa */
  indeterminate: PropTypes.bool,
  /**
Indica la percentuale di progresso da mostrare.

Ogni elemento può essere indicato con un valore (la percentuale) o con un oggetto (percentage e color).
Il colore può essere una classe tra quelle gestite ('alarm', 'warning', 'success', 'info', 'safe') un colore rgb (#373a3c).

Vedi `Progress.Colors`.
*/
  percentage: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.number),
    PropTypes.shape({
      percentage: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
    }),
    PropTypes.arrayOf(PropTypes.number),
    PropTypes.arrayOf(
      PropTypes.shape({
        percentage: PropTypes.number.isRequired,
        color: PropTypes.string.isRequired,
      }),
    ),
  ]),
  /** Testo da mostrare sotto la progress bar */
  text: PropTypes.string,
  /**
Colore di fondo della progress bar

Il colore può essere una classe tra quelle gestite ('alarm', 'warning', 'success', 'info', 'safe') un colore rgb (#373a3c).
  */
  color: PropTypes.string,
};

Progress.defaultProps = {
  className: '',
  indeterminate: false,
  percentage: 0,
  text: '',
  color: undefined,
};

export default Progress;
